import React from 'react';

import { Dialog, Theme } from '@material-ui/core';
import { createStyles, withStyles, WithStyles } from '@material-ui/core/styles';
import { Lock as LockIcon, LockOpen as LockOpenIcon } from '@material-ui/icons';

import { ButtonGroup, SvgIconWrapper, Tooltip, Button } from 'embeds/components';

import { useDialogContext } from '../FiltersDialogContext';

import FilterAppointmentStatus from './FilterAppointmentStatus';
import FilterAppointmentType from './FilterAppointmentType';
import FilterDateAggregation from './FilterDateAggregation';
import FilterFacility from './FilterFacility';
import FilterFormProcessingStatus from './FilterFormProcessingStatus';
import FilterGroup from './FilterGroup';
import FilterInsurance from './FilterInsurance';
import FilterInsuranceVerification from './FilterInsuranceVerification';
import FilterIntegrationFlags from './FilterIntegrationFlags';
import FilterModality from './FilterModality';
import FilterPatientForm from './FilterPatientForm/FilterPatientForm';
import FilterProvider from './FilterProvider';
import FilterProviders from './FilterProviders';
import FilterReminderType from './FilterReminderType';
import FiltersRoles from './FilterRoles';
import FiltersApprover from './FiltersApprover';
import FiltersApptTypeFeatures from './FiltersApptTypeFeatures';
import FiltersDate from './FiltersDate';
import FilterSentToEhrStatus from './FilterSentToEhrStatus';
import FiltersFacilityFeatures from './FiltersFacilityFeatures';
import FiltersMessageChannel from './FiltersMessageChannel';
import FiltersMessageRef from './FiltersMessageRef';
import FiltersMessageStatus from './FiltersMessageStatus';
import FiltersMessageType from './FiltersMessageType';
import FiltersProvidersFeatures from './FiltersProvidersFeatures';
import FiltersReferralStatus from './FiltersReferralStatus';
import FiltersSender from './FiltersSender';
import FiltersSidebarDialog from './FiltersSidebarDialog';
import FilterStatusSource from './FilterStatusSource';
import FilterUser from './FilterUser';
import FilterUserStatus from './FilterUserSatus';
import FilterUserType from './FilterUserType';

const styles = ({
  spacing: { unit },
  palette: {
    common: { white },
  },
  custom: {
    palette: { colorVeryLightGray },
  },
}: Theme) =>
  createStyles({
    dialogWrapper: {
      display: 'flex',
      minHeight: 388,
      overflow: 'hidden',
    },
    dialogContent: {
      width: '70%',
      backgroundColor: white, // add to theme?
    },
    dialogContentFooter: {
      display: 'flex',
      justifyContent: 'flex-end',
      padding: `14px ${unit * 3}px`,
      backgroundColor: colorVeryLightGray, // add to theme?
      height: unit * 8,
      width: '70%',
      position: 'absolute',
      bottom: 0,
      left: '30%',
    },
    buttonLock: {
      paddingLeft: 2,
      paddingRight: 2,
    },
    cancelButton: {
      fontWeight: 600,
      marginRight: unit * 2,
      width: 60,
    },
  });

export interface DialogProps extends WithStyles<typeof styles> {
  filterName: string;
  showHiddenResources?: boolean;
}

const FiltersDialog: React.FC<DialogProps> = ({ classes, filterName, showHiddenResources }) => {
  const {
    toggleDialogOpen,
    dialogOpen,
    selectedTab,
    toggleLockFilters,
    handleApplyFilters,
    isFilterLocked,
    hideLockButton,
  } = useDialogContext();

  const handleOnApplyFiltersClick = () => {
    handleApplyFilters();
    toggleDialogOpen();
  };

  return (
    <Dialog maxWidth="md" open={dialogOpen} fullWidth>
      <div className={classes.dialogWrapper}>
        <FiltersSidebarDialog startCaseLabel />
        <div className={classes.dialogContent}>
          {(selectedTab === 'dateRange' || selectedTab === 'additionalDateRange') && (
            <FiltersDate filterName={selectedTab} />
          )}
          {selectedTab === 'dateRangeFuture' && (
            <FiltersDate filterName={selectedTab} options={['today', 'tomorrow', 'nextWeek', 'custom']} disablePast />
          )}
          {selectedTab === 'broadcastListDateRange' && (
            <FiltersDate
              filterName={selectedTab}
              options={['any', 'today', 'yesterday', 'pastWeek', 'pastMonth', 'pastYear', 'custom']}
            />
          )}
          {selectedTab === 'referringProvider' && <FilterProvider type="inbound" />}
          {selectedTab === 'renderingProvider' && <FilterProvider type="outbound" />}
          {selectedTab === 'referralStatus' && <FiltersReferralStatus referralType={filterName} />}
          {selectedTab === 'user' && <FilterUser />}
          {selectedTab === 'group' && <FilterGroup />}
          {(selectedTab === 'role' || selectedTab === 'roles') && <FiltersRoles type={selectedTab} />}
          {selectedTab === 'status' && <FilterUserStatus />}
          {selectedTab === 'formProcessingStatus' && <FilterFormProcessingStatus />}
          {selectedTab === 'sentToEhrStatus' && <FilterSentToEhrStatus />}
          {selectedTab === 'provider' && <FilterProvider type="default" showHiddenResources={showHiddenResources} />}
          {/* NOTE: FilterProviders lists all providers hidden and visible, not the same as FilterProvider */}
          {selectedTab === 'providers' && <FilterProviders />}
          {selectedTab === 'facility' && <FilterFacility showHiddenResources={showHiddenResources} />}
          {selectedTab === 'statusSource' && <FilterStatusSource />}
          {selectedTab === 'apptType' && <FilterAppointmentType showHiddenResources={showHiddenResources} />}
          {selectedTab === 'apptStatus' && <FilterAppointmentStatus />}
          {selectedTab === 'formName' && <FilterPatientForm type="formName" />}
          {selectedTab === 'forms' && <FilterPatientForm type="forms" />}
          {selectedTab === 'ccdaForms' && <FilterPatientForm type="ccdaForms" />}
          {selectedTab === 'schedulingPlus' && <FilterPatientForm type="schedulingPlus" />}
          {selectedTab === 'insuranceVerification' && <FilterInsuranceVerification />}
          {selectedTab === 'insurance' && <FilterInsurance />}
          {selectedTab === 'modality' && <FilterModality />}
          {selectedTab === 'providerFeatures' && <FiltersProvidersFeatures />}
          {selectedTab === 'facilityFeatures' && <FiltersFacilityFeatures />}
          {selectedTab === 'apptTypeFeatures' && <FiltersApptTypeFeatures />}
          {selectedTab === 'messageStatus' && <FiltersMessageStatus />}
          {selectedTab === 'messageType' && <FiltersMessageType />}
          {selectedTab === 'messageRef' && <FiltersMessageRef />}
          {selectedTab === 'messageChannel' && <FiltersMessageChannel />}
          {selectedTab === 'reminderType' && <FilterReminderType />}
          {selectedTab === 'apptTypeAttributes' && (
            <FilterIntegrationFlags type={selectedTab} isProviderResource={false} />
          )}
          {selectedTab === 'facilityAttributes' && (
            <FilterIntegrationFlags type={selectedTab} isProviderResource={false} />
          )}
          {selectedTab === 'providerAttributes' && <FilterIntegrationFlags type={selectedTab} isProviderResource />}
          {selectedTab === 'integrationFlags' && (
            <FilterIntegrationFlags isProviderResource={filterName === 'providersPracticeResources'} />
          )}
          {selectedTab === 'userType' && <FilterUserType />}
          {selectedTab === 'sender' && <FiltersSender />}
          {selectedTab === 'approver' && <FiltersApprover />}
          {selectedTab === 'dateAggregation' && <FilterDateAggregation filterName={selectedTab} />}
        </div>
        <div className={classes.dialogContentFooter}>
          <Button
            className={classes.cancelButton}
            color="primary"
            onClick={() => toggleDialogOpen({ shouldApplySnapshotOnClose: true })}
            size="large"
            variant="text"
          >
            Cancel
          </Button>
          <ButtonGroup variant="contained" color="primary">
            {!hideLockButton && (
              <Tooltip title={isFilterLocked ? 'Filters locked' : 'Lock in your filters'} placement="top" hideArrow>
                <Button className={classes.buttonLock} onClick={toggleLockFilters}>
                  {isFilterLocked ? (
                    <SvgIconWrapper Icon={LockIcon} color="success" fontSize="small" />
                  ) : (
                    <LockOpenIcon fontSize="small" />
                  )}
                </Button>
              </Tooltip>
            )}
            <Button onClick={handleOnApplyFiltersClick}>Apply Filters</Button>
          </ButtonGroup>
        </div>
      </div>
    </Dialog>
  );
};

export default withStyles(styles)(FiltersDialog);
